import React, { useEffect, useRef, memo } from 'react'
import { useStore } from '@/src/types/store'
import { ChartPeriod, resolveChartPeriod } from './HomepageEdit/components/ChartPeriodField'

/**
 * TradingView spells the ranges its own way: the range the chart opens on is
 * the suffix on the symbol ("ASX:XYZ|12M").
 */
const TRADING_VIEW_RANGE: Record<ChartPeriod, string> = {
  '1D': '1D',
  '1M': '1M',
  '3M': '3M',
  '1Y': '12M',
  ALL: 'ALL'
}

interface TradingViewWidgetProps {
  /**
   * Period the admin chose for this section (RL-2448). Undefined or null means
   * nothing has been saved, and the chart keeps the range it showed before the
   * setting existed.
   */
  defaultPeriod?: string | null
}

const TradingViewWidget: React.FC<TradingViewWidgetProps> = ({ defaultPeriod }) => {
  const container = useRef<HTMLDivElement | null>(null)
  const { homeTheme } = useStore()

  useEffect(() => {
    if (!homeTheme) return
    const sanitizeTicker = (v: string) => (/^[A-Z0-9._-]+$/i.test(v) ? v : 'DEFAULT')

    const code = sanitizeTicker(homeTheme.code)
    const symbolRange = TRADING_VIEW_RANGE[resolveChartPeriod(defaultPeriod, homeTheme?.code)]
    const script = document.createElement('script')
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-symbol-overview.js'
    script.type = 'text/javascript'
    script.async = true
    script.innerHTML = `
      {
        "symbols": [
          [
            "ASX:${code}|${symbolRange}"
          ]
        ],
        "chartOnly": false,
        "width": "100%",
        "height": "100%",
        "locale": "en",
        "colorTheme": "light",
        "autosize": true,
        "showVolume": true,
        "showMA": false,
        "hideDateRanges": false,
        "hideMarketStatus": false,
        "hideSymbolLogo": true,
        "scalePosition": "right",
        "scaleMode": "Normal",
        "fontFamily": "-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif",
        "fontSize": "10",
        "noTimeScale": false,
        "valuesTracking": "1",
        "changeMode": "price-and-percent",
        "chartType": "line",
        "headerFontSize": "medium",
        "volumeUpColor": "#D8D8D8",
        "volumeDownColor": "#D8D8D8",
        "lineWidth": 3,
        "lineType": 0,
        "dateRanges": [
          "1d|1",
          "1m|30",
          "3m|60",
          "12m|1D",
          "60m|1W",
          "all|1M"
        ],
        "color" : "#888888"
      }`
    const node = container.current
    if (node) {
      // The embed renders itself into the container, so wipe whatever a
      // previous run left behind before re-injecting; otherwise changing the
      // period would stack a second chart under the first.
      node.innerHTML = '<div class="tradingview-widget-container__widget"></div>'
      node.appendChild(script)
    }

    return () => {
      // Cleanup script if needed
      if (node && script.parentNode) {
        node.removeChild(script)
      }
    }
  }, [homeTheme.code, defaultPeriod])

  return (
    <div className={`tradingview-widget-container overflow-hidden`} ref={container}>
      <div className="tradingview-widget-container__widget"></div>
    </div>
  )
}

export default memo(TradingViewWidget)
